// packages/frontend/src/services/healthCheckService.ts
// Note: useLogger is a factory function, not a React hook. This is a service file, not a component.

import { useLogger as createLoggerFunction, type Logger } from "../utils/logger";
import {
  GENAI_BASE_URL,
  IMAGE_STORAGE_BASE_URL,
} from "../config/endpoints";

// Environment configuration
const CLOUD_RUN_GENAI_SERVICE_URL = GENAI_BASE_URL;
const CLOUD_RUN_IMAGE_STORAGE_SERVICE_URL = IMAGE_STORAGE_BASE_URL;

// Helper function to create logger (avoids React hooks linter false positive)
// This is NOT a React component - createLoggerFunction is a factory function, not a hook
function createLogger(): Logger {
  return createLoggerFunction("HealthCheckService");
}

// Health check response interface
export interface HealthCheckResponse {
  status: string;
  timestamp: string;
  service: string;
  version?: string;
  environment?: string;
  database?: {
    status: string;
    responseTime?: number;
  };
  uptime?: number;
  memory?: {
    rss: number;
    heapTotal: number;
    heapUsed: number;
    external: number;
    arrayBuffers: number;
  };
  responseTime?: number;
}

// Health check result interface
export interface HealthCheckResult {
  success: boolean;
  data?: HealthCheckResponse;
  error?: string;
  timestamp: string;
  serviceName?: string; // Add service name to identify which service
}

// Multi-service health check result
export interface MultiServiceHealthCheckResult {
  genAiApi: HealthCheckResult;
  imageStorage: HealthCheckResult;
  timestamp: string;
}

/**
 * Health Check Service
 * Provides functionality to check the health of Cloud Run services
 */
export class HealthCheckService {
  private static instance: HealthCheckService;
  private readonly timeout: number;
  private readonly logger: Logger;

  private constructor() {
    this.timeout = 10000; // 10 seconds timeout
    // Initialize logger using helper function to avoid React hooks linter false positive
    this.logger = createLogger();
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): HealthCheckService {
    if (!HealthCheckService.instance) {
      HealthCheckService.instance = new HealthCheckService();
    }
    return HealthCheckService.instance;
  }

  /**
   * Perform health check on a specific service
   */
  async checkServiceHealth(
    serviceUrl: string,
    serviceName: string
  ): Promise<HealthCheckResult> {
    const startTime = Date.now();
    const healthEndpoint = `${serviceUrl}/health`;

    this.logger.debug("Performing health check", {
      endpoint: healthEndpoint,
      serviceName,
      timeout: this.timeout,
      component: "HealthCheckService",
    });

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(healthEndpoint, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        mode: "no-cors", // Disable CORS for health check since it should be silent
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;

      if (!response.ok) {
        const errorMessage = `Health check failed with status ${response.status}: ${response.statusText}`;

        this.logger.debug("Health check failed", {
          serviceName,
          status: response.status,
          statusText: response.statusText,
          responseTime,
          component: "HealthCheckService",
        });

        return {
          success: false,
          error: errorMessage,
          timestamp: new Date().toISOString(),
          serviceName,
        };
      }

      const healthData: HealthCheckResponse = await response.json();

      this.logger.debug("Health check successful", {
        serviceName,
        status: healthData.status,
        service: healthData.service,
        responseTime,
        component: "HealthCheckService",
      });

      return {
        success: true,
        data: {
          ...healthData,
          responseTime, // Add our measured response time
        },
        timestamp: new Date().toISOString(),
        serviceName,
      };
    } catch (error) {
      const responseTime = Date.now() - startTime;
      let errorMessage = "Health check failed";
      let isCorsError = false;

      if (error instanceof Error) {
        if (error.name === "AbortError") {
          errorMessage = `Health check timed out after ${this.timeout}ms`;
        } else if (
          error.message.includes("CORS") ||
          error.message.includes("Access-Control-Allow-Origin")
        ) {
          errorMessage = "CORS policy blocked health check request";
          isCorsError = true;
        } else if (error.message === "Failed to fetch") {
          // This is often a CORS error in disguise
          errorMessage = "Network error (possibly CORS-related)";
          isCorsError = true;
        } else {
          errorMessage = error.message;
        }
      }

      // Use different log levels based on error type
      if (isCorsError) {
        this.logger.debug("Health check blocked by CORS (expected in production)", {
          serviceName,
          error: errorMessage,
          responseTime,
          endpoint: healthEndpoint,
          component: "HealthCheckService",
        });
      } else {
        this.logger.debug("Health check error", {
          serviceName,
          error: errorMessage,
          responseTime,
          component: "HealthCheckService",
        });
      }

      return {
        success: false,
        error: errorMessage,
        timestamp: new Date().toISOString(),
        serviceName,
      };
    }
  }

  /**
   * Perform health check on both services in parallel (non-blocking)
   * Replaces the old single-service check
   */
  async checkAllServicesHealth(): Promise<MultiServiceHealthCheckResult> {
    this.logger.debug("Starting multi-service health check", {
      component: "HealthCheckService",
      services: ["gen-ai-api", "image-storage"],
    });

    // Check both services in parallel (non-blocking)
    const [genAiResult, imageStorageResult] = await Promise.allSettled([
      this.checkServiceHealth(CLOUD_RUN_GENAI_SERVICE_URL, "gen-ai-api"),
      this.checkServiceHealth(
        CLOUD_RUN_IMAGE_STORAGE_SERVICE_URL,
        "image-storage"
      ),
    ]);

    const genAi =
      genAiResult.status === "fulfilled"
        ? genAiResult.value
        : {
            success: false,
            error:
              genAiResult.reason instanceof Error
                ? genAiResult.reason.message
                : String(genAiResult.reason || "Unknown error"),
            timestamp: new Date().toISOString(),
            serviceName: "gen-ai-api",
          };

    const imageStorage =
      imageStorageResult.status === "fulfilled"
        ? imageStorageResult.value
        : {
            success: false,
            error:
              imageStorageResult.reason instanceof Error
                ? imageStorageResult.reason.message
                : String(imageStorageResult.reason || "Unknown error"),
            timestamp: new Date().toISOString(),
            serviceName: "image-storage",
          };

    this.logger.debug("Multi-service health check completed", {
      component: "HealthCheckService",
      genAiApi: {
        success: genAi.success,
        status: genAi.data?.status,
      },
      imageStorage: {
        success: imageStorage.success,
        status: imageStorage.data?.status,
      },
    });

    return {
      genAiApi: genAi,
      imageStorage: imageStorage,
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Legacy method for backward compatibility (checks gen-ai-api only)
   * @deprecated Use checkAllServicesHealth() instead
   */
  async checkHealth(): Promise<HealthCheckResult> {
    return this.checkServiceHealth(CLOUD_RUN_GENAI_SERVICE_URL, "gen-ai-api");
  }

  /**
   * Perform health check with retry logic
   * Designed to be completely silent and non-blocking
   */
  async checkHealthWithRetry(
    maxRetries: number = 2,
    retryDelay: number = 1000
  ): Promise<HealthCheckResult> {
    let lastResult: HealthCheckResult = {
      success: false,
      error: "No attempts made",
      timestamp: new Date().toISOString(),
    };

    for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
      this.logger.debug("Health check attempt", {
        attempt,
        maxAttempts: maxRetries + 1,
        component: "HealthCheckService",
      });

      try {
        lastResult = await this.checkHealth();

        if (lastResult.success) {
          if (attempt > 1) {
            this.logger.debug("Health check succeeded after retry", {
              attempt,
              component: "HealthCheckService",
            });
          }
          return lastResult;
        }

        // If this was the last attempt, don't wait
        if (attempt <= maxRetries) {
          this.logger.debug("Health check failed, retrying", {
            attempt,
            retryDelay,
            error: lastResult.error,
            component: "HealthCheckService",
          });

          await new Promise((resolve) => setTimeout(resolve, retryDelay));
        }
      } catch (error) {
        // Create error result for any unexpected errors during retry
        lastResult = {
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Unknown error during retry",
          timestamp: new Date().toISOString(),
        };

        if (attempt <= maxRetries) {
          this.logger.debug("Health check error, retrying", {
            attempt,
            retryDelay,
            error: lastResult.error,
            component: "HealthCheckService",
          });

          await new Promise((resolve) => setTimeout(resolve, retryDelay));
        }
      }
    }

    this.logger.debug("Health check completed after all attempts", {
      attempts: maxRetries + 1,
      success: lastResult.success,
      component: "HealthCheckService",
    });

    return lastResult;
  }

  /**
   * Get the health endpoint URL (legacy - returns gen-ai-api)
   * @deprecated
   */
  getHealthEndpoint(): string {
    return `${CLOUD_RUN_GENAI_SERVICE_URL}/health`;
  }

  /**
   * Check if health check is configured properly
   */
  isConfigured(): boolean {
    return Boolean(
      CLOUD_RUN_GENAI_SERVICE_URL && CLOUD_RUN_IMAGE_STORAGE_SERVICE_URL
    );
  }
}

// Export singleton instance
export const healthCheckService = HealthCheckService.getInstance();

// Export default
export default healthCheckService;
